import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderOpen,
  GraduationCap,
  Settings,
  Building2,
  ShieldAlert,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Bell,
  LogOut,
} from 'lucide-react';
import { cn, titleCaseName } from '@/lib/utils';
import { TrinetraLoop3 } from '@/components/icons/trinetra-icons';
import { useOrgContext } from '@/providers/org-provider';
import { useAuth } from '@/providers/auth-provider';
import { OrgSwitcher } from '@/components/layout/org-switcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Single-column sidebar. Layout, top to bottom:
 *
 *   [logo + collapse handle]
 *   [org switcher]
 *   [org-scoped nav]            ← always rendered when there's a currentOrg,
 *                                  even on global routes like /settings.
 *   [global nav: Learn]
 *   [activity indicator + bell]
 *   [admin entry, if isAdmin]
 *   <flex spacer>
 *   [user menu]                 ← avatar + email + sign-out dropdown.
 */
export function Sidebar() {
  const { pathname } = useLocation();
  const { currentOrg } = useOrgContext();
  const { user, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const orgId = currentOrg?.orgId ?? null;

  const orgScopedItems = orgId
    ? [
        { name: 'Dashboard', href: `/orgs/${orgId}/dashboard`, icon: LayoutDashboard },
        { name: 'Portfolios', href: `/orgs/${orgId}/portfolios`, icon: FolderOpen },
        { name: 'Compliance Posture', href: `/orgs/${orgId}/tep`, icon: ShieldCheck },
        { name: 'Org Settings', href: `/orgs/${orgId}/settings`, icon: Building2 },
      ]
    : [];

  const globalItems = [
    { name: 'Learn', href: '/my/learn', icon: GraduationCap },
    { name: 'Settings', href: '/my/settings', icon: Settings },
  ];

  const adminItem = user?.isAdmin
    ? { name: 'Admin', href: '/admin', icon: ShieldAlert }
    : null;

  // Cognito-supplied displayName is often lowercase ("murali"); we render
  // it title-cased everywhere it appears as a human name so the UI stays
  // consistent regardless of how the upstream IdP stored it.
  const displayName = titleCaseName(user?.displayName) || null;
  const initial =
    displayName?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    'U';

  return (
    <aside
      className={cn(
        'relative hidden md:flex flex-col border-r border-border bg-card text-card-foreground transition-all duration-300',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Brand. Same px-2 outer + px-3 inner pattern as every nav row
          below so the logo's left edge aligns with each row's icon. */}
      <div className="h-16 px-2 flex items-center border-b border-border">
        <Link
          to={orgId ? `/orgs/${orgId}/dashboard` : '/home'}
          className={cn(
            'flex items-center gap-3 rounded-lg min-w-0 hover:bg-muted transition-colors',
            collapsed ? 'p-2 justify-center w-full' : 'px-3 py-2',
          )}
        >
          <TrinetraLoop3 className="h-6 w-6 text-primary-strong shrink-0" />
          {!collapsed && (
            <span className="font-display text-lg font-semibold tracking-wide text-foreground truncate">Trinetra</span>
          )}
        </Link>
      </div>

      {/* Org switcher — same px-2 outer wrapper. */}
      <div className="px-2 pt-3 pb-2 border-b border-border">
        <OrgSwitcher collapsed={collapsed} />
      </div>

      {/* Nav body */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-1 px-2">
        {/* Org-scoped */}
        {orgScopedItems.map((item) => (
          <NavLink key={item.name} item={item} pathname={pathname} collapsed={collapsed} />
        ))}

        {orgScopedItems.length > 0 && !collapsed && (
          <div className="my-2 mx-1 border-t border-border" />
        )}

        {/* Global */}
        {globalItems.map((item) => (
          <NavLink key={item.name} item={item} pathname={pathname} collapsed={collapsed} />
        ))}

        {!collapsed && <div className="my-2 mx-1 border-t border-border" />}

        {/* Activity + notifications — kept always visible per UX
            preference. Bell is a placeholder until notifications land.
            In expanded mode the bell sits on its own row matching the
            other nav items (icon + label); collapsed mode shrinks both
            controls to icon-only. */}
        {/* Notifications. The agent-activity indicator used to live
            here too, but its dropdown overflowed the sidebar edge —
            it now floats at the top-right of the viewport (rendered
            from AppLayout). */}
        <NavLink
          item={{ name: 'Notifications', href: '/my/notifications', icon: Bell }}
          pathname={pathname}
          collapsed={collapsed}
        />

        {adminItem && (
          <>
            {!collapsed && <div className="my-2 mx-1 border-t border-border" />}
            <Link
              to={adminItem.href}
              title={collapsed ? adminItem.name : undefined}
              className={cn(
                'flex items-center gap-3 rounded-lg text-sm font-medium transition-colors',
                collapsed ? 'p-2 justify-center' : 'px-3 py-2',
                pathname.startsWith(adminItem.href)
                  ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                  : 'text-muted-foreground hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400',
              )}
            >
              <adminItem.icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{adminItem.name}</span>}
            </Link>
          </>
        )}
      </nav>

      {/* User menu (bottom). Same px-2 outer + px-3 inner pattern as the
          rest of the sidebar so the avatar's left edge lines up. */}
      <div className="border-t border-border px-2 py-2">
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(
              'w-full flex items-center gap-3 rounded-lg hover:bg-muted text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
              collapsed ? 'p-2 justify-center' : 'px-3 py-2',
            )}
          >
            <span className="h-6 w-6 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <span className="text-xs font-semibold text-primary-strong">{initial}</span>
            </span>
            {!collapsed && (
              <span className="flex-1 min-w-0 text-left">
                <span className="block text-sm font-medium text-foreground truncate">
                  {displayName ?? user?.email ?? 'You'}
                </span>
                {user?.email && (
                  <span className="block text-[11px] text-muted-foreground truncate">{user.email}</span>
                )}
              </span>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align={collapsed ? 'start' : 'end'} side={collapsed ? 'right' : 'top'} className="w-56">
            <div className="px-3 py-2">
              <p className="text-sm font-medium text-foreground">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer p-0">
              <Link to="/my/settings" className="flex items-center gap-2 px-2 py-1.5 w-full">
                <Settings className="h-4 w-4" /> Account Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()} className="text-destructive cursor-pointer">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Collapse / expand handle, pinned to the bottom-right edge of
          the sidebar so the same control toggles both directions and
          doesn't take up sidebar real estate. Half-overlaps the right
          border. */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="absolute bottom-4 -right-3 z-20 h-6 w-6 rounded-full border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted shadow-sm flex items-center justify-center"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
      </button>
    </aside>
  );
}

function NavLink({
  item,
  pathname,
  collapsed,
}: {
  item: { name: string; href: string; icon: React.ComponentType<{ className?: string }> };
  pathname: string;
  collapsed: boolean;
}) {
  const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      title={collapsed ? item.name : undefined}
      className={cn(
        'flex items-center gap-3 rounded-lg text-sm font-medium transition-colors',
        // Expanded: padded so icon left-edge aligns with the brand /
        // switcher / user-menu icons. Collapsed: center icons in the
        // narrow rail.
        collapsed ? 'p-2 justify-center' : 'px-3 py-2',
        isActive
          ? 'bg-primary/15 text-primary-strong'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon className="h-5 w-5 flex-shrink-0" />
      {!collapsed && <span>{item.name}</span>}
    </Link>
  );
}
